import { Clock, SlidersHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { Popover, PopoverContent, PopoverTrigger, SegmentedControl, Tooltip } from "@nexus/ui";
import { getFilterImpactHint } from "@/components/dashboard/FilterImpactInfo";
import { useAppContext } from "@/contexts/AppContext";
import {
  ANALYTICS_TIME_RANGE_OPTIONS,
  getAnalyticsTimeRangeShortLabel,
  canChooseDashboardTimeRange,
  dashboardTimeRangePreset,
  resolveViewerTimeRange,
  shortPeriodLabel,
  defaultRefresh,
} from "@/lib/dashboard/timeRange";
import { useDashboardRefresh } from "@/hooks/dashboard/useAutoRefresh";
import type { DashboardScopeConfig } from "@/lib/dashboard/types";
import { resolveDashboardTimeRange } from "@/lib/dashboard/timeRange";
import {
  formatCompactDateRange,
  formatContextDateRange,
  formatDateRangeTooltip,
} from "@/lib/dateFormat";
import { storedDashboardTenantIds } from "@/lib/dashboard/scope";
import { reportTenantState } from "@/lib/dashboard/reportScope";
import { cn } from "@/lib/utils";
import { EventScopeSelector } from "./EventScopeSelector";
import { useGenericEventScope } from "./useGenericEventScope";
import { TenantScopeSelector } from "./TenantScopeSelector";
import { usePageScopeControls, type PagePreviewFilter } from "./usePageScopeControls";

type TimeRange = (typeof ANALYTICS_TIME_RANGE_OPTIONS)[number];

interface TimeRangeControlProps {
  value: TimeRange | "default";
  onChange: (value: TimeRange) => void;
  className?: string;
  coverageHint?: string;
  defaultLabel?: string;
  onDefault?: () => void;
}

/**
 * One of the page header's scope controls — tenant, event or time range —
 * rendered only when the current page uses it. The header places the slots in
 * dependency order. The time range collapses to a popover when the header
 * (`@container/pageheader`) is narrow.
 */
export function PageScopeControls({
  previewFilters,
  slot,
  description,
}: {
  previewFilters?: readonly PagePreviewFilter[];
  slot: "tenant" | "event" | "time" | "period" | "tenant-context";
  description?: ReactNode;
}) {
  const { analyticsTimeRange, setAnalyticsTimeRange, timezone } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    showTenant,
    showTimeRange,
    showEvents,
    forceAllTenants,
    dashboardRecord,
    dashboardWidgets,
    dashboardFilters,
    authorizedTenants,
  } = usePageScopeControls(previewFilters);
  const policy = dashboardRecord?.config.scope?.timeRange;
  const periodRefreshTime = useDashboardRefresh(
    dashboardRecord?.config.refresh ?? defaultRefresh("reporting"),
    slot === "period" && policy?.mode === "scheduled",
    policy,
  );

  if (slot === "tenant-context") {
    if (!dashboardRecord || showTenant) return null;
    const reports = reportTenantState(dashboardWidgets);
    const tenantIds =
      storedDashboardTenantIds(dashboardRecord.config.scope) ??
      (reports.mixed ? [] : reports.ids.map(String));
    if (tenantIds.length !== 1) return null;
    const tenant = authorizedTenants?.find((entry) => entry.tenant_id === tenantIds[0]);
    if (
      !tenant ||
      (typeof description === "string" &&
        description.toLocaleLowerCase().includes(tenant.tenant_label.toLocaleLowerCase()))
    )
      return null;
    return (
      <span
        data-page-fixed-tenant
        className="min-w-0 max-w-full truncate font-normal text-secondary"
        title={tenant.tenant_label}
      >
        {tenant.tenant_label}
      </span>
    );
  }

  if (slot === "event") {
    return showEvents ? (
      <div data-page-event className="min-w-0 shrink-0">
        <GenericEventScopeControl scope={dashboardRecord?.config.scope} />
      </div>
    ) : null;
  }

  if (slot === "tenant") {
    return showTenant ? (
      <div data-page-tenant className="min-w-0 max-w-full shrink-0">
        <TenantScopeSelector
          forcedAll={forceAllTenants}
          coverageHint={
            dashboardRecord
              ? getFilterImpactHint(dashboardWidgets, dashboardFilters, "tenants")
              : undefined
          }
        />
      </div>
    ) : null;
  }

  const hasTimePicker =
    showTimeRange && (!policy || canChooseDashboardTimeRange(policy, dashboardFilters));
  if (slot === "period") {
    if (!policy || hasTimePicker) return null;
    const range = resolveDashboardTimeRange(
      policy,
      policy.mode === "scheduled" ? periodRefreshTime : Date.now(),
    );
    const fullRange = range
      ? formatDateRangeTooltip(new Date(range.from), new Date(range.to), timezone)
      : undefined;
    const period = (
      <span
        data-page-period
        className={cn(
          "min-w-0 max-w-full font-normal text-secondary",
          policy.mode === "scheduled" ? "whitespace-normal text-sm" : "truncate",
        )}
        title={policy.mode === "scheduled" ? undefined : fullRange}
        aria-label={fullRange}
        tabIndex={policy.mode === "scheduled" ? 0 : undefined}
      >
        {range
          ? policy.mode === "scheduled"
            ? formatContextDateRange(new Date(range.from), new Date(range.to), timezone)
            : formatCompactDateRange(new Date(range.from), new Date(range.to), timezone)
          : policy.mode === "scheduled"
            ? "Period unavailable"
            : "Event period"}
      </span>
    );
    return policy.mode === "scheduled" && fullRange ? (
      <Tooltip content={fullRange}>{period}</Tooltip>
    ) : (
      period
    );
  }
  if (!hasTimePicker) return null;
  if (showTimeRange && canChooseDashboardTimeRange(policy, dashboardFilters)) {
    const effectivePolicy =
      resolveViewerTimeRange(policy, dashboardFilters, searchParams.get("time_range")) ?? policy;
    const value = dashboardTimeRangePreset(effectivePolicy) ?? "default";
    const defaultLabel = shortPeriodLabel(
      resolveDashboardTimeRange(policy, Date.now())?.label ?? "Dashboard default",
    );
    return (
      <ResponsiveTimeRangeControl
        value={value}
        defaultLabel={dashboardTimeRangePreset(policy) ? undefined : defaultLabel}
        onDefault={() => {
          const query = new URLSearchParams(searchParams);
          query.delete("time_range");
          setSearchParams(query);
        }}
        onChange={(selection) => {
          const query = new URLSearchParams(searchParams);
          if (selection === dashboardTimeRangePreset(policy)) query.delete("time_range");
          else query.set("time_range", selection);
          setSearchParams(query);
        }}
      />
    );
  }
  const timeHint = dashboardRecord
    ? getFilterImpactHint(dashboardWidgets, dashboardFilters, "time-range")
    : undefined;
  return (
    <ResponsiveTimeRangeControl
      value={analyticsTimeRange}
      onChange={setAnalyticsTimeRange}
      coverageHint={timeHint}
    />
  );
}

function ResponsiveTimeRangeControl(props: TimeRangeControlProps) {
  return (
    <div data-page-time className="min-w-0 shrink-0">
      <div className="hidden @min-[64rem]/pageheader:block">
        <AnalyticsTimeRangeControl {...props} />
      </div>
      <div className="@min-[64rem]/pageheader:hidden">
        <CompactTimeRangeControl {...props} />
      </div>
    </div>
  );
}

function CompactTimeRangeControl({
  value,
  onChange,
  coverageHint,
  defaultLabel,
  onDefault,
}: TimeRangeControlProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-control border border-control-border bg-surface px-3 text-sm font-medium text-primary transition-colors hover:border-control-border-hover focus-visible:outline-2 focus-visible:outline-focus"
          aria-label="Time range"
          title={coverageHint}
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          <span>Time range</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={8}
        aria-label="Time range"
        className="w-[min(22rem,calc(100vw-1rem))] p-3"
      >
        <AnalyticsTimeRangeControl
          value={value}
          onChange={onChange}
          defaultLabel={defaultLabel}
          onDefault={onDefault}
          className="w-full justify-between"
          coverageHint={coverageHint}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The event scope pill for a dashboard that turned on the editor's "Events"
 * viewer control — same compact component as Event Monitor/Overview, driven
 * by the generic resolver instead of their own richer, page-specific hooks.
 */
function GenericEventScopeControl({ scope }: { scope?: DashboardScopeConfig }) {
  const eventScope = useGenericEventScope(scope);
  return <EventScopeSelector {...eventScope} />;
}

export function AnalyticsTimeRangeControl({
  value,
  onChange,
  className,
  coverageHint,
  defaultLabel,
  onDefault,
}: TimeRangeControlProps) {
  const options = [
    ...ANALYTICS_TIME_RANGE_OPTIONS.map((option) => ({
      value: option,
      label: getAnalyticsTimeRangeShortLabel(option),
      title: option,
    })),
    ...(defaultLabel ? [{ value: "default", label: defaultLabel, title: defaultLabel }] : []),
  ];

  return (
    <div
      title={coverageHint ?? "Time range"}
      className={cn(
        "flex h-10 items-center gap-2 rounded-control border border-control-border bg-surface pl-3 pr-1",
        className,
      )}
    >
      <Clock className="size-4 shrink-0 text-muted" aria-hidden />
      <SegmentedControl
        label="Analytics time range"
        value={value}
        onChange={(selection) => {
          const preset = ANALYTICS_TIME_RANGE_OPTIONS.find((option) => option === selection);
          if (preset) onChange(preset);
          else onDefault?.();
        }}
        options={options}
        className="h-8 border-0 bg-transparent p-0"
      />
    </div>
  );
}
